import { useState, useMemo } from 'react';
import { CREDIT_CATEGORIES } from '@troia-v3/types';
import type { CreditCategory, TransactionListParams } from '@troia-v3/types';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Select } from '@/shared/components/ui/Select';
import { ListPagePagination } from '@/shared/components/ui/ListPagePagination';
import { StatusPill } from '@/shared/components/ui/StatusPill';
import { Icon } from '@/shared/components/ui';
import { ConsumptionChart } from './ConsumptionChart';
import { useConsumptionByDay, useChannelConsumption, useTransactions } from '../hooks/useCredits';

export function ConsumptionStatement() {
  const { data: consumption, isLoading: loadingChart } = useConsumptionByDay();
  const { data: channelConsumption } = useChannelConsumption();

  // Transaction filters
  const [filters, setFilters] = useState<TransactionListParams>({ page: 1, limit: 20 });
  const { data: transactions, isLoading: loadingTx } = useTransactions(filters);

  const numberFormat = new Intl.NumberFormat('pt-BR');

  const maxTotal = useMemo(() => {
    if (!consumption?.totals?.length) return 0;
    return Math.max(...consumption.totals.map((t) => t.total));
  }, [consumption]);

  const maxChannelTotal = useMemo(() => {
    if (!channelConsumption?.length) return 0;
    return Math.max(...channelConsumption.map((c) => c.total));
  }, [channelConsumption]);

  const categoryOptions = useMemo(() => {
    const opts = [{ value: '', label: 'Todas as categorias' }];
    for (const [key, config] of Object.entries(CREDIT_CATEGORIES)) {
      opts.push({ value: key, label: config.label });
    }
    return opts;
  }, []);

  return (
    <div className="space-y-6">
      {/* 1. Gráfico de consumo diário */}
      {loadingChart ? (
        <Card className="rounded-xl"><CardContent className="px-4 py-12 text-center text-sm" style={{ color: 'var(--theme-muted-text)' }}>Carregando...</CardContent></Card>
      ) : consumption && consumption.items.length > 0 ? (
        <ConsumptionChart data={consumption.items} totals={consumption.totals} />
      ) : (
        <Card className="rounded-xl"><CardContent className="px-4 py-12 text-center text-sm" style={{ color: 'var(--theme-muted-text)' }}>Nenhum consumo no período</CardContent></Card>
      )}

      {/* 2. Consumo por categoria */}
      {consumption && consumption.totals.length > 0 && (
        <Card className="rounded-xl">
          <CardHeader className="pb-2 py-3 px-4">
            <div className="flex items-center gap-2">
              <Icon name="bar-chart" className="h-4 w-4" style={{ color: 'var(--theme-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>Consumo por Categoria</span>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {consumption.totals.map((item) => {
                const config = CREDIT_CATEGORIES[item.category];
                const pct = maxTotal > 0 ? (item.total / maxTotal) * 100 : 0;
                return (
                  <div key={item.category} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                        {config?.label || item.category}
                      </span>
                      <span className="text-sm font-semibold ml-2" style={{ color: 'var(--theme-primary)' }}>
                        {numberFormat.format(item.total)}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--theme-card-border)' }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: 'var(--theme-primary)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3. Consumo por canal */}
      {channelConsumption && channelConsumption.length > 0 && (
        <Card className="rounded-xl">
          <CardHeader className="pb-2 py-3 px-4">
            <div className="flex items-center gap-2">
              <Icon name="message-circle" className="h-4 w-4" style={{ color: 'var(--theme-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>Consumo por Canal</span>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {channelConsumption.map((item) => {
                const pct = maxChannelTotal > 0 ? (item.total / maxChannelTotal) * 100 : 0;
                return (
                  <div key={item.channelId} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                        {item.channelName}
                      </span>
                      <span className="text-sm font-semibold ml-2" style={{ color: 'var(--theme-primary)' }}>
                        {numberFormat.format(item.total)}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--theme-card-border)' }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: 'var(--theme-primary)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 4. Tabela de transações */}
      <Card className="rounded-xl">
        <CardHeader className="pb-2 py-3 px-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Icon name="list" className="h-4 w-4" style={{ color: 'var(--theme-primary)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>Transações</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-48">
                <Select
                  options={categoryOptions}
                  value={filters.category || ''}
                  onChange={(val) => setFilters((f) => ({ ...f, category: (val || undefined) as CreditCategory | undefined, page: 1 }))}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {loadingTx ? (
            <div className="py-8 text-center text-sm" style={{ color: 'var(--theme-muted-text)' }}>Carregando...</div>
          ) : !transactions || transactions.items.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: 'var(--theme-muted-text)' }}>Nenhuma transação encontrada</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <th className="text-left py-2 font-medium text-xs" style={{ color: 'var(--theme-muted-text)' }}>Data</th>
                      <th className="text-left py-2 font-medium text-xs" style={{ color: 'var(--theme-muted-text)' }}>Categoria</th>
                      <th className="text-left py-2 font-medium text-xs" style={{ color: 'var(--theme-muted-text)' }}>Tipo</th>
                      <th className="text-right py-2 font-medium text-xs" style={{ color: 'var(--theme-muted-text)' }}>Créditos</th>
                      <th className="text-right py-2 font-medium text-xs" style={{ color: 'var(--theme-muted-text)' }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.items.map((tx) => {
                      const config = CREDIT_CATEGORIES[tx.category];
                      const isDebit = tx.type === 'debit';
                      return (
                        <tr key={tx.id} style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                          <td className="py-2" style={{ color: 'var(--theme-card-text)' }}>
                            {tx.createdAt
                              ? new Date(tx.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                              : '-'}
                          </td>
                          <td className="py-2" style={{ color: 'var(--theme-card-text)' }}>
                            {config?.label || tx.category}
                          </td>
                          <td className="py-2">
                            {/* F62: pill de status via StatusPill shared */}
                            <StatusPill
                              variant={isDebit ? 'error' : 'success'}
                              label={isDebit ? 'Débito' : 'Crédito'}
                              data-testid={`transaction-type-${tx.id}`}
                            />
                          </td>
                          <td className="py-2 text-right font-medium" style={{ color: isDebit ? '#ef4444' : '#22c55e' }}>
                            {isDebit ? '-' : '+'}{numberFormat.format(tx.amount)}
                          </td>
                          <td className="py-2 text-right" style={{ color: 'var(--theme-card-text)' }}>
                            {numberFormat.format(tx.balanceAfter)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination — shared numerada (F50) */}
              <ListPagePagination
                currentPage={filters.page || 1}
                totalPages={transactions.totalPages}
                onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
                totalItems={transactions.total}
                pageSize={filters.limit || 20}
                itemLabel="transações"
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
